import { ScheduleType } from '../activities/activity-type.model';
import { DayStatusCycleKind, ScheduleGroup } from '../settings/me.model';
import { AllocationRow } from './day.store';
import {
  DayStatusCycleGroup,
  DayStatusCycles,
  resolveCycleDisplayLabel,
  resolveScheduleGroupCycle
} from './day-status-cycles.util';
import { nextIdAfter } from './habit-sort.util';
import { resolveScheduleGroupLabel } from './schedule-group.util';

export type GroupRow = AllocationRow & {
  notThisCycle?: boolean;
};

export type GroupRowTier = 'active' | 'notThisCycle' | 'done';

export type GroupCapsule = {
  ordinal: ScheduleType;
  label: string;
  cycle: DayStatusCycleKind;
  doneThisCycle: boolean;
  running: boolean;
  ariaLabel: string;
};

export function resolveGroupRowTier(row: Pick<GroupRow, 'doneThisCycle' | 'notThisCycle'>): GroupRowTier {
  if (row.notThisCycle) {
    return 'notThisCycle';
  }
  if (row.doneThisCycle) {
    return 'done';
  }
  return 'active';
}

export function sortGroupRows(rows: readonly GroupRow[], oldestFirst: boolean): GroupRow[] {
  const activeRows = rows.filter((row) => resolveGroupRowTier(row) === 'active');
  const setAsideRows = rows.filter((row) => resolveGroupRowTier(row) === 'notThisCycle');
  const doneRows = rows.filter((row) => resolveGroupRowTier(row) === 'done');

  return [
    ...(oldestFirst ? sortByDaysSinceDesc(activeRows) : [...activeRows]),
    ...sortByDaysSinceDesc(setAsideRows),
    ...sortByDaysSinceDesc(doneRows)
  ];
}

// The Hidden view and "Hide for today" were removed (LUM-157), so the legacy per-day hidden flag no
// longer filters rows: anything hidden earlier the same day would otherwise be unreachable.
export function buildVisibleGroupRows(rows: readonly GroupRow[], oldestFirst: boolean): GroupRow[] {
  return sortGroupRows(rows, oldestFirst);
}

export interface UpNextActivity {
  activity: GroupRow;
  group: DayStatusCycleGroup;
  cycleLabel: string;
}

/**
 * Up next on Today: the first open activity (not done, not set aside) of the first unfinished group,
 * in the user's Day Status order (Work cycle before Life), using that group page's own order.
 */
export function resolveUpNextActivity(
  cycles: DayStatusCycles,
  rowsForGroup: (ordinal: ScheduleType) => readonly GroupRow[],
  oldestFirst: boolean
): UpNextActivity | null {
  for (const section of [cycles.work, cycles.life]) {
    for (const group of section.groups) {
      if (group.doneThisCycle) {
        continue;
      }
      const activity = sortGroupRows(rowsForGroup(group.ordinal), oldestFirst)
        .find((row) => resolveGroupRowTier(row) === 'active');
      if (activity) {
        return { activity, group, cycleLabel: section.label };
      }
    }
  }
  return null;
}

/** "Deep work · 0 of 2 slots": the activity's group, then today's slots when any are planned. */
export function buildUpNextMetaLabel(upNext: UpNextActivity): string {
  const { activity, group } = upNext;
  return activity.currentSlots > 0
    ? `${group.label} · ${activity.completedSlots} of ${activity.currentSlots} slots`
    : group.label;
}

export function nextOpenGroupActivityTypeId(
  rows: readonly GroupRow[],
  currentActivityTypeId: string,
  oldestFirst: boolean
): string | null {
  const sorted = sortGroupRows(rows, oldestFirst);
  const current = sorted.find((row) => row.activityTypeId === currentActivityTypeId);
  if (!current) {
    return null;
  }
  const openOrderedIds = sorted
    .filter((row) => resolveGroupRowTier(row) === 'active')
    .map((row) => row.activityTypeId);
  if (resolveGroupRowTier(current) !== 'active') {
    // Opened from a Done or set-aside row: those sort below every open row, so carry on with the first open one.
    return openOrderedIds[0] ?? null;
  }
  return nextIdAfter(currentActivityTypeId, openOrderedIds);
}

export function countNotThisCycle(rows: readonly Pick<GroupRow, 'notThisCycle'>[]): number {
  return rows.filter((row) => !!row.notThisCycle).length;
}

export function buildGroupCapsules(
  groups: readonly ScheduleGroup[],
  doneByOrdinal: ReadonlyMap<ScheduleType, boolean>,
  runningOrdinal: ScheduleType | null
): GroupCapsule[] {
  return groups.map((group) => {
    const label = resolveScheduleGroupLabel(group);
    const running = runningOrdinal === group.ordinal;
    return {
      ordinal: group.ordinal,
      label,
      cycle: resolveScheduleGroupCycle(group),
      doneThisCycle: doneByOrdinal.get(group.ordinal) ?? false,
      running,
      ariaLabel: running ? `${label}, running now` : label
    };
  });
}

export function splitCapsulesByCycle(capsules: readonly GroupCapsule[]): {
  work: GroupCapsule[];
  life: GroupCapsule[];
} {
  return {
    work: capsules.filter((capsule) => capsule.cycle === 'WORK'),
    life: capsules.filter((capsule) => capsule.cycle === 'LIFE')
  };
}

export function buildGroupMetaLine(options: {
  cycle: DayStatusCycleKind;
  workCycleLabel: string | null;
  lifeCycleLabel: string | null;
  doneCount: number;
  totalCount: number;
  notThisCycleCount: number;
  oldestFirst: boolean;
}): string {
  const bits = [
    `${resolveCycleDisplayLabel(options.cycle, options.workCycleLabel, options.lifeCycleLabel)} cycle`,
    `${options.doneCount} of ${options.totalCount} done`
  ];
  if (options.notThisCycleCount > 0) {
    bits.push(`${options.notThisCycleCount} not this cycle`);
  }
  if (options.oldestFirst) {
    bits.push('Oldest first');
  }
  return bits.join(' · ');
}

function sortByDaysSinceDesc<T extends { daysSince: number | null }>(rows: readonly T[]): T[] {
  return [...rows].sort((left, right) => {
    const leftDays = left.daysSince == null ? Number.POSITIVE_INFINITY : left.daysSince;
    const rightDays = right.daysSince == null ? Number.POSITIVE_INFINITY : right.daysSince;
    return rightDays - leftDays;
  });
}
